import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";

// Components
import { Pill, TableView, useTablePagination, DynamicTooltip, Icons } from "@gpstrackit/gtc-react-controls";
import { Timezone } from 'Components/uiControls';
import DateRangeInfo from './modals/dateRangeModal';

// Material UI
import { Grid } from "@mui/material";
import { Card } from '@mui/material';

// Actions
import { searchCoachingHistoryByDriver, searchRiskHistory, searchStatsSinceLastCoaching } from './redux/slice';
import { sendGenericExport } from "Core/common/redux/actions";

//Helpers
import { checkDeviceData } from "Core/data/Helpers";

const OverviewTab = (props) => {
    const { messages } = useIntl();
    const dispatch = useDispatch();

    //Redux states
    const loadingCoachingHistory = useSelector((state) => state.driverPerformanceOverviewTabRedux.loadingCoachingHistory);
    const coachingHistory = useSelector((state) => state.driverPerformanceOverviewTabRedux.coachingHistory);
    const nextToken = useSelector((state) => state.driverPerformanceOverviewTabRedux.nextToken);
    const statsSinceLastCoaching = useSelector((state) => state.driverPerformanceOverviewTabRedux.statsSinceLastCoaching);
    const loadingStatsSinceLastCoaching = useSelector((state) => state.driverPerformanceOverviewTabRedux.loadingStatsSinceLastCoaching);
    const dateRange = useSelector((state) => state.driverPerformanceOverviewTabRedux.dateRange);
    const caseDetail = useSelector((state) => state.driverPerformanceCaseDetailRedux.caseDetail);
    const riskHistory = useSelector((state) => state.driverPerformanceOverviewTabRedux.riskHistory);
    const loadingRiskHistory = useSelector((state) => state.driverPerformanceOverviewTabRedux.loadingRiskHistory);
    const [page, setPage] = useState(1);

    const user = useSelector(state => state.securityRedux.user);
    const { dateformat, timeZone, isKph } = user;

    const handlerEvents = (event) => {
        if (!event?.events || event.events.length === 0) {
            return 0;
        }

        // Filter events excluding those marked as "falsepositive" or "noissue"
        const validEvents = event.events.filter(eventItem => {
            try {
                // Parse deviceData string to access caseEventStatus
                const deviceData = checkDeviceData(eventItem.deviceData);

                const caseEventStatus = deviceData?.caseEventStatus?.toLowerCase();

                // Exclude events marked as false positive or no issue
                return caseEventStatus !== 'falsepositive' && caseEventStatus !== 'noissue';
            } catch (error) {
                return true;
            }
        });

        return validEvents.length;
    }

    //Local states for tables pagination
    const [tablesState, setTablesState] = useState({
        risk: {
            currentPage: 1,
            rowsPerPage: 10,
            data: [],
            total: 0,
            loading: false
        },
        coachingHistory: {
            currentPage: 1,
            rowsPerPage: 8,
            data: [],
            total: 0,
            loading: false
        },
        statsSinceLastCoaching: {
            currentPage: 1,
            rowsPerPage: 10,
            data: [],
            total: 0,
            loading: false
        }
    });
    // Refs
    const riskTable = useRef();
    const coachingHistoryTable = useRef();
    const statsSinceLastCoachingTable = useRef();

    const columnsCoachingHistory = [
        {
            title: messages['driver_performance_case_overview_caseId'],
            key: 'caseId',
            width: 100,
            render: (row) => <div>{row?.caseNumber}</div>,
            show: true
        },
        {
            title: messages['driver_performance_case_overview_status'],
            key: 'status',
            width: 130,
            render: (row) => {
                const item = {
                    ...row?.status,
                    label: messages[`driver_performance_cases_status_${row?.status?.label?.toLowerCase()}`] || row?.status?.label,
                    color: row?.status?.label === 'Expired' ? "black" : row?.status?.label === 'Pending' ? "grey" : row?.status?.label === 'Evaluate' ? "orange" : "#66bb6a"
                }

                return (
                    <div style={{ width: 110 }} className="casesTab-badge-status">
                        <Pill
                            key={row?.id}
                            item={item}
                            variant={'outlined'}
                        />
                    </div>
                )
            },
            show: true
        },
        {
            title: messages['driver_performance_case_overview_created'], key: 'created', width: 200, render: (row) => <div>{row?.createdOn ? <Timezone
                date={row?.createdOn}
                dateFormat={dateformat}
                timeFormat="hh:mm a"
                timezone={timeZone}
            /> : <></>}</div>, show: true
        },
        { title: messages['driver_performance_case_overview_piSuccess'], key: 'piSuccess', width: 130, render: (row) => <div>{row?.status?.nextStep == "PI Successfull" ? messages['driver_performance_case_yes'] : messages["driver_performance_case_no"]}</div>, show: true },
    ];

    const columnsRisk = [
        { title: messages['driver_performance_case_overview_eventName'], key: 'event', width: 150, render: (row) => <div>{row?.eventName ? messages[`driver_performance_${row?.eventName?.replace(/\s+/g, "")}`] || row?.eventName : 'N/A'}</div>, show: true, },
        { title: messages['driver_performance_case_overview_count'], key: 'count', width: 100, render: (row) => <div>{handlerEvents(row) || 0}</div>, show: true },
        { title: messages['driver_performance_case_overview_priorCoaching'], key: 'priorCoaching', width: 130, render: (row) => <div>{row?.priorCoaching ? messages['driver_performance_case_yes'] : messages['driver_performance_case_no']}</div>, show: true },
    ];

    const columnsStateSinceLastCoaching = [
        { title: messages['driver_performance_case_overview_metric'], key: 'metric', width: 120, render: (row) => <div>{messages[`driver_performance_case_${row.key}`] || row.key}</div>, show: true, },
        {
            title: messages['driver_performance_case_overview_measure'],
            key: 'measure',
            width: 130,
            render: (row) => {
                let value = row.value;
                if (isKph && row.key === 'totalDistance') {
                    if (parseInt(row.value) > 0) {
                        value = (row.value * 1.609344);
                    }
                }
                // Format float values to show only 2 decimal places
                const formattedValue = value !== null && value !== undefined
                    ? parseFloat(value).toFixed(2)
                    : value;
                return <div>{`${formattedValue} ${row.key === 'totalDistance' ? isKph ? messages['kilometers'] : messages["miles"] : ''}`}</div>;
            },
            show: true
        },
    ];

    const paginationCases = useTablePagination({
        dispatch,
        action: searchCoachingHistoryByDriver,
        searchFields: ["openSearch"],
        tableRef: coachingHistoryTable,
        sendGenericExport,
        exportName: "casesTab",
    });

    useEffect(() => {
        paginationCases.setColumns(columnsCoachingHistory);
        coachingHistoryTable?.current?.setPage(1);
        coachingHistoryTable?.current?.setSelectedItems([]);
    }, []);

    useEffect(() => {
        if (caseDetail?.driverId && !caseDetail?.updateCoach) {
            // Reset coaching history table and load initial data
            updateTableState('coachingHistory', { currentPage: 1 });
            paginationCases.refresh({
                _conditions: [
                    { fields: ['driverId'], terms: [caseDetail?.driverId], exact: true },
                    { page: 1 }
                ]
            });
            dispatch(searchStatsSinceLastCoaching({ caseId: caseDetail?.id, driverId: caseDetail?.driverId }));
        }
        if (caseDetail?.caseNumber && !caseDetail?.updateCoach) {
            dispatch(searchRiskHistory({ caseNumber: caseDetail?.caseNumber }));
        }
    }, [caseDetail, dispatch]);

    // Helper functions for table pagination
    const updateTableState = (tableName, updates) => {
        setTablesState(prev => ({
            ...prev,
            [tableName]: {
                ...prev[tableName],
                ...updates
            }
        }));
    };

    const handlePageChange = (tableName, page) => {
        updateTableState(tableName, { currentPage: page });
        if (tableName === 'coachingHistory' && caseDetail?.driverId) {
            paginationCases.refresh({
                _conditions: [
                    { fields: ['driverId'], terms: [caseDetail?.driverId], exact: true },
                    { page: page }
                ]
            });
        }
        // TODO: Add handlers for other tables when their actions are implemented
    };

    const handleRowsPerPageChange = (tableName, newRowsPerPage) => {
        updateTableState(tableName, {
            rowsPerPage: newRowsPerPage,
            currentPage: 1
        });
        // TODO: Add handlers for other tables when their actions are implemented
    };

    return (
        <Grid container spacing={2} style={{ marginTop: 5 }}>
            <Grid item xs={12} md={6}>
                <Card style={{ padding: 16, height: 365 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <span style={{ fontWeight: 600 }}>{messages['driver_performance_case_overview_risk']}</span>
                            <div style={{ marginTop: 10 }}>
                                <TableView
                                    ref={riskTable}
                                    columns={columnsRisk}
                                    data={riskHistory || []}
                                    loading={loadingRiskHistory}
                                    total={riskHistory?.length || 0}
                                    disableItemSelection
                                    hideDragable
                                    height={365}
                                    topSpaceSize={300}
                                    onChangePage={(page) => handlePageChange('risk', page)}
                                    onChangeRowsPerPage={(newRowsPerPage) => handleRowsPerPageChange('risk', newRowsPerPage)}
                                    hidePagination
                                />
                            </div>
                        </Grid>
                    </Grid>
                </Card>
            </Grid>
            <Grid item xs={12} md={6}>
                <Card style={{ padding: 16, height: 365 }}><Grid container spacing={2}>
                    <Grid item xs={12}>
                        <span style={{ fontWeight: 600 }}>{messages['driver_performance_case_overview_coachingHistory']}</span>
                        <div style={{ marginTop: 10 }}>
                            <TableView
                                {...paginationCases}
                                data={coachingHistory || []}
                                loading={loadingCoachingHistory}
                                columns={columnsCoachingHistory}
                                disableItemSelection
                                hideDragable
                                height={300}
                                topSpaceSize={325}
                                onChangePage={() => {
                                    if (coachingHistory?.length) {
                                        let newPage = page + 1
                                        setPage(newPage);
                                        handlePageChange('coachingHistory', newPage)
                                    }
                                }}
                                infiniteScroll
                                nextToken={nextToken}
                                noMoreItemsMessage={messages?.['driver_performance_noMoreItems']}
                            />
                        </div>
                    </Grid>
                </Grid>
                </Card>
            </Grid>
            <Grid item style={{ width: 400 }}>
                <Card style={{ padding: 16, height: 315 }}><Grid container spacing={2}>
                    <Grid item xs={12}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <span style={{ fontWeight: 600 }}>{messages['driver_performance_case_overview_performanceHistory']}</span>
                            {dateRange && (
                                <DynamicTooltip
                                    trigger={<Icons name='info' style={{ fontSize: 16, color: '#666', cursor: 'pointer', display: 'flex' }} />}
                                    openOn="hover"
                                    size="small"
                                    position="top"
                                >
                                    {() => (
                                        <DateRangeInfo
                                            dateRange={dateRange}
                                            dateformat={dateformat}
                                            timeZone={timeZone}
                                        />
                                    )}
                                </DynamicTooltip>
                            )}
                        </div>
                        <div style={{ marginTop: 10 }}>
                            <TableView
                                ref={statsSinceLastCoachingTable}
                                columns={columnsStateSinceLastCoaching}
                                data={statsSinceLastCoaching}
                                loading={loadingStatsSinceLastCoaching}
                                total={4}
                                disableItemSelection
                                hideDragable
                                height={325}
                                topSpaceSize={300}
                                hidePagination
                            />
                        </div>
                    </Grid>
                </Grid>
                </Card>
            </Grid>
        </Grid>
    )
}

export default OverviewTab;